import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GridLegacy as Grid,
} from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import EditableGroup, { EditableGroupContext, EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import { ITreeViewCommand, TreeViewCommand } from "../../../components/treeView/TreeView";
import {
  AssetLocation,
  AssetLocationTypes,
  api,
  type IAssetLocationUpdate,
  RequestStatus,
} from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import { LocationFields } from "../components/LocationFields";
import { useTranslation } from "react-i18next";
import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { isAdmin, isSysAdmin } from "../../../utility/utils";
import { validateKeyLocation } from "../utils/keyStorageValidation";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import AvailabilityDateField from "../components/AvailabilityDateField";
import { isFutureLocalDate, toLocalMidnightUtc } from "../utils/availabilityDate";
import { usePreferences } from "../../../utility/usePreferences";

const buildUpdateParams = (location: AssetLocation): IAssetLocationUpdate => ({
  id: location.id,
  name: location.name,
  customerFacingName: location.customerFacingName,
  iata: location.iata,
  parentId: location.parentId,
  type: location.type,
  keyType: location.keyType,
  capacity: location.capacity,
  blockPark: location.blockPark === true,
  selfPark: location.selfPark === true,
  returns: location.returns === true,
  arrivals: location.arrivals === true,
  carWash: location.carWash === true,
  vip: location.vip === true,
  color: location.color,
  carParkId: location.carParkId,
  code: location.code,
  isRestrictedToVehicleLocation: location.isRestrictedToVehicleLocation === true,
  isAutoMoveLocation: location.isAutoMoveLocation === true,
  noMixedLaneAllocations: location.noMixedLaneAllocations,
  vehicleMovementRuleAction: location.vehicleMovementRuleAction,
  keyDEALRuleAction: location.keyDEALRuleAction,
  keyscanRestriction: location.keyscanRestriction,
  alertIfOverfilled: location.alertIfOverfilled,
  keyStorageRule: location.keyStorageRule,
  keyStorageDate: location.keyStorageDate,
  keyStorageTimeslotId: location.keyStorageTimeslotId,
  allowedParentIds: location.allowedParentIds,
  isAllocatable: Boolean(location.isAllocatable),
  isCollectionPoint: location.isCollectionPoint === true,
  isDeliveryPoint: location.isDeliveryPoint === true,
  gpsLocation: location.gpsLocation,
});

const haveSameUpdateParams = (a: AssetLocation, b: AssetLocation) =>
  JSON.stringify(buildUpdateParams(a)) === JSON.stringify(buildUpdateParams(b));


const LocationEditDialog: React.FunctionComponent<{
  location: AssetLocation;
  show: boolean;
  onClose: (command: ITreeViewCommand | null) => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { timeZone } = usePreferences();
  const { hasPermission } = useUserPermissions();
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const canUpdateStatus = isRbacReleased
    ? hasPermission("assetTracking.location.updateStatus")
    : isSysAdmin(session) || isAdmin(session);

  const deleteLocationFromHierarchyEnabled = useReleaseManagement(ReleaseFlag.releaseDeleteLocationFromHierarchy);
  const isZoneAvailabilityReleased = useReleaseManagement(ReleaseFlag.releaseOpeningAndClosingDateZoneSettings);
  const showAvailability = isZoneAvailabilityReleased && props.location.type === AssetLocationTypes.Zone;

  const [location, setLocation] = useState<AssetLocation | null>(null);
  const [originalLocation, setOriginalLocation] = useState<AssetLocation | null>(null);
  const [parentLocation, setParentLocation] = useState<AssetLocation | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const [isGpsLocationValid, setIsGpsLocationValid] = useState(true);
  const [isAvailabilityInputValid, setIsAvailabilityInputValid] = useState(true);
  const [showPartialSaveError, setShowPartialSaveError] = useState(false);


  const updateLocation = api.hooks.assetLocation.update();
  const fetchParentById = api.hooks.assetLocation.getById();
  const reloadLocation = api.hooks.assetLocation.getById();
  const setLocationActive = api.hooks.assetLocation.setActive();
  const isSaving = updateLocation.status === RequestStatus.loading
    || setLocationActive.status === RequestStatus.loading
    || reloadLocation.status === RequestStatus.loading;

  const toUtcIso = (date: Date | null | undefined) =>
    date ? toLocalMidnightUtc(date, timeZone).toISOString() : null;

  const scheduleMatches = (a: AssetLocation, b: AssetLocation) =>
    toUtcIso(a.openingDate) === toUtcIso(b.openingDate)
    && toUtcIso(a.closingDate) === toUtcIso(b.closingDate);

  const closeWithReload = () => {
    props.onClose({
      command: TreeViewCommand.reload,
      payLoad: null,
      targetId: null,
    });
  };

  const save = () => {
    if (location == null || originalLocation == null || isSaving) {
      return;
    }

    if (showAvailability && canUpdateStatus) {
      const date = location.active ? location.closingDate : location.openingDate;
      if (!isAvailabilityInputValid || !isFutureLocalDate(date, timeZone)) {
        return;
      }
    }

    const hasManagePermission = hasPermission("assetTracking.location.manage");
    const detailsChanged = hasManagePermission && !haveSameUpdateParams(location, originalLocation);
    const activeChanged = (deleteLocationFromHierarchyEnabled || showAvailability)
      && originalLocation.active !== location.active;
    const scheduleChanged = showAvailability
      && canUpdateStatus
      && (activeChanged
        ? toUtcIso(location.active ? location.closingDate : location.openingDate) !== null
        : !scheduleMatches(location, originalLocation));
    const updateNeeded = detailsChanged || scheduleChanged;
    const saveStatusBeforeUpdate = activeChanged && scheduleChanged;

    if (!updateNeeded && !activeChanged) {
      props.onClose(null);
      return;
    }

    if (detailsChanged) {
      const errorMsg = validateKeyLocation(location, t);
      if (errorMsg) {
        setErr(new Error(errorMsg));
        return;
      }
    }

    setShowPartialSaveError(false);
    const updateParams = buildUpdateParams(location);
    if (scheduleChanged) {
      updateParams.openingDate = location.active ? null : toUtcIso(location.openingDate);
      updateParams.closingDate = location.active ? toUtcIso(location.closingDate) : null;
    }

    const recoverPartialSave = () => {
      setShowPartialSaveError(true);
      reloadLocation.send(session.current.context, { id: props.location.id }, (authoritativeLocation) => {
        if (authoritativeLocation) {
          setLocation({ ...authoritativeLocation });
          setOriginalLocation({ ...authoritativeLocation });
        }
      });
    };

    const saveUpdate = (previousStepSaved: boolean, onSuccess: () => void) => {
      updateLocation.send(session.current.context, updateParams, (updatedLocation) => {
        if (updatedLocation) {
          onSuccess();
        } else if (previousStepSaved) {
          recoverPartialSave();
        }
      });
    };

    const saveStatus = (previousStepSaved: boolean, onSuccess: () => void) => {
      setLocationActive.send(session.current.context, {
        id: location.id,
        active: location.active,
      }, (success) => {
        if (success) {
          onSuccess();
        } else if (previousStepSaved) {
          recoverPartialSave();
        }
      });
    };

    // The stacked update API validates the date against the persisted status,
    // so a date for a newly selected status must be saved after setActive.
    if (saveStatusBeforeUpdate) {
      saveStatus(false, () => saveUpdate(true, closeWithReload));
    } else if (updateNeeded) {
      saveUpdate(false, () => {
        if (activeChanged) {
          saveStatus(true, closeWithReload);
        } else {
          closeWithReload();
        }
      });
    } else if (activeChanged) {
      saveStatus(false, closeWithReload);
    }
  }

  const handleCancel = () => {
    if (originalLocation) {
      setLocation({ ...originalLocation });
    }
    props.onClose(null);
  };

  useEffect(() => {
    if (props.show == true) {
      setLocation(null);
      setOriginalLocation(null)
      setParentLocation(null);
      setIsGpsLocationValid(true);
      setIsAvailabilityInputValid(true);
      setShowPartialSaveError(false);
      if (props.location.parentId == null) {
        setParentLocation(null);
        setLocation({ ...props.location });
        setOriginalLocation({ ...props.location });
      } else {
        fetchParentById.send(session.current.context, {
          id: props.location.parentId
        }, (parent) => {
          if (parent == null || parent.id == null) {
            setParentLocation(null);
          } else {
            setParentLocation(parent);
          }
          setLocation({ ...props.location });
          setOriginalLocation({ ...props.location });
        });
      }
    }
  }, [props.show]);

  const availabilityDate = location && showAvailability
    ? (location.active ? location.closingDate : location.openingDate)
    : null;
  const isAvailabilityValid = isAvailabilityInputValid && (!availabilityDate
    || isFutureLocalDate(availabilityDate, timeZone));

  const activeToggle = location && (
    <EditableGroup<AssetLocation>
      id="availabilitySettings"
      testId="location-edit-active-group"
      editType={canUpdateStatus ? EditableGroupType.alwaysEditing : EditableGroupType.none}
      data={location}
      onDataChanged={async (loc) => {
        setLocation(loc);
        return true;
      }}
    >
      <Grid container alignItems="flex-end">
        <EditableField<AssetLocation, boolean>
          data-testid="active-edit-location-switch"
          inputType={EditableFieldTypes.switch}
          columnWidth={6}
          label={t("locations.active")}
          onProvide={(data) => data.active ?? false}
          onChange={(data, value) => {
            data.active = value;
            setIsAvailabilityInputValid(true);
            if (showAvailability) {
              if (value) {
                data.openingDate = null;
              } else {
                data.closingDate = null;
              }
            }
          }}
        />
        {showAvailability && (
          <EditableGroupContext.Consumer>
            {({ data, setData, isEditing }) => {
              const draft = data as AssetLocation;
              const dateField = draft.active ? "closingDate" : "openingDate";
              const date = draft[dateField];

              return (
                <Grid item xs={12} sm={6}>
                  <AvailabilityDateField
                    key={dateField}
                    disabled={!isEditing}
                    inputIsValid={isAvailabilityInputValid}
                    label={t(`locations.${dateField}`)}
                    testId={`location-${dateField}-date-picker`}
                    value={date}
                    onInputValidityChange={setIsAvailabilityInputValid}
                    onChange={(value) => setData?.({ ...draft, [dateField]: value })}
                  />
                </Grid>
              );
            }}
          </EditableGroupContext.Consumer>
        )}
      </Grid>
    </EditableGroup>
  );

  return <Dialog open={props.show}>
    <DialogTitle>
      {deleteLocationFromHierarchyEnabled && !showAvailability ? <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Fragment>{t("locations.editLocation")}</Fragment>
        {activeToggle}
      </div> : <Fragment>{t("locations.editLocation")}</Fragment>}
    </DialogTitle>
    <DialogContent>
      <ErrorDialog onClose={() => setErr(null)} err={err}></ErrorDialog>
      <ProgressDialog status={updateLocation.status}></ProgressDialog>
      <ErrorDialog err={updateLocation.err}></ErrorDialog>
      {!showPartialSaveError && <ErrorDialog err={setLocationActive.err}></ErrorDialog>}
      <ErrorDialog
        err={showPartialSaveError ? new Error(t("locations.errors.detailsSavedAvailabilityFailed")) : null}
        onClose={() => setShowPartialSaveError(false)}
      />
      <ErrorDialog err={reloadLocation.err}></ErrorDialog>
      {location != null && (
        <Fragment>
          <div style={{ marginTop: 0 }}>
            <b>{props.location.code}</b>
          </div>
          <div style={{ marginTop: 20 }}>

            <EditableGroup<AssetLocation>
              id="locSettings"
              testId="location-edit-group"
              editType={hasPermission("assetTracking.location.manage") ? EditableGroupType.alwaysEditing : EditableGroupType.none}
              data={location}
              onDataChanged={async (loc) => {
                setLocation(loc);
                return true;
              }}
            >
              <Grid container>
                <LocationFields
                  location={location}
                  parentLocation={parentLocation}
                  session={session}
                  onGpsLocationValidityChange={setIsGpsLocationValid}
                />
              </Grid>
            </EditableGroup>

          </div>
          {showAvailability && (
            <Fragment>
              <div style={{ width: "100%", marginTop: 20, marginBottom: 20 }}>
                <b>{t("locations.availability")}</b>
              </div>
              {activeToggle}
            </Fragment>
          )}
        </Fragment>
      )}

    </DialogContent>
    <DialogActions>
      <Button data-testid="cancel-edit-location-button" disabled={isSaving} onClick={handleCancel}>
        {t("general.cancel")}
      </Button>
      <Button data-testid="save-edit-location-button" disabled={isSaving || location == null || location.name.length == 0 || !isGpsLocationValid || (showAvailability && canUpdateStatus && !isAvailabilityValid) || (!hasPermission("assetTracking.location.manage") && !hasPermission("assetTracking.location.updateStatus"))} onClick={() => {
        save();
      }}>
        <b>{t("general.save")}</b>
      </Button>
    </DialogActions>
  </Dialog>
}

export default LocationEditDialog
